'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  cn,
  decodeBase64,
  emailSchema,
  isDefined,
  Price,
  PriceRange,
  resolveCatchMessage,
  storeConfigSelector,
  TCatchMessage,
  useGetFormilyBlockQuery,
  useLoadingContext,
  useSubscribeEmailMutation,
  useToastContext,
  z,
} from '@segway/core'
import { BundleProduct } from '@segway/core/src/graphql/generated/graphql'
import { useAppSelector } from '@segway/core/src/store/hooks'
import { useClickAway, useToggle } from 'ahooks'
import { useForm } from 'react-hook-form'

import {
  Arrow,
  CopyIcon,
  CouponIcon,
  EmailIcon,
  ExpandableHTML,
  InStock,
  OutOfStock,
} from '@/components'
import { useRouter } from '@/i18n/navigation'
import { Button, Input } from '@/ui'

import { BundleProducts, useProduct, useProductData } from '../../ProductProvider'
import { AffirmWidget } from '../affirmWidget'
import { DetailPopup } from '../detailPopup'
import { ReviewTotalWidget } from '../reviewTotalWidget'

type SubscriptionBlockData = {
  title: string
  background_pc: string
  background_mob: string
}

const ProductInfo: React.FC = () => {
  const {
    totalPrice,
    isInStock,
    isBundle,
    isSubscribe,
    addProductAlert,
    selectedBundle,
    responsive,
    productAffirmData,
    setSelectedBundle,
  } = useProduct()
  const product = useProductData()

  const baseProductSelectOption = useMemo(() => {
    return {
      sku: product?.sku,
      name: product?.name,
      url_key: product?.url_key,
      sw_simple_description: product?.sw_simple_description,
      affirm_additional_text: product?.affirm_data,
      small_image: product?.small_image,
      price_range: product?.price_range,
    }
  }, [product])

  const router = useRouter()
  const toast = useToastContext()
  const loading = useLoadingContext()
  const t = useTranslations('Cart')
  const tCommon = useTranslations('Common')
  const storeConfig = useAppSelector(storeConfigSelector)

  const pdpSubscriptionEnableCode = useMemo(() => {
    return product?.formily_content
      ? product?.formily_content?.find((item) => item?.code === 'enable_subscribe')?.data
      : '0'
  }, [product])

  const [tempProduct, setTempProduct] = useState<BundleProducts | null>(null)
  const [showDetailPopup, { toggle: toggleDetailPopup }] = useToggle(false)
  const [showTip, { toggle: toggleTip }] = useToggle(false)
  const tipRef = useRef<HTMLParagraphElement>(null)

  const [subscribeEmail, { isLoading }] = useSubscribeEmailMutation()
  const { data } = useGetFormilyBlockQuery({ identify: 'subscribe_collection' })

  const isShowReviewWidget = useMemo(() => {
    return (
      product?.yotpoMessage?.isEnabled &&
      product.yotpoMessage?.isRenderWidget &&
      product.yotpoMessage?.isRenderBottomline
    )
  }, [product])

  const subscriptionBlockData = useMemo(() => {
    if (data && data.formilyBlock) {
      const subscribeBlock = data.formilyBlock.block_data

      if (!subscribeBlock) {
        return { title: '', background_pc: '', background_mob: '' }
      }

      const pdpSubscribeBlockData: SubscriptionBlockData = JSON.parse(subscribeBlock)?.pdp

      return pdpSubscribeBlockData
    }
  }, [data])

  const couponData = useMemo(() => {
    const data = product?.formily_content?.find((item) => item?.code === 'coupon')?.data

    if (!data) {
      return { is_enable: '0', title: '', code: '' }
    }

    return JSON.parse(data)
  }, [product?.formily_content])

  useClickAway(() => {
    if (showTip) {
      toggleTip()
    }
  }, tipRef)

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  })

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(couponData.code)
      toast.show({
        icon: 'success',
        content: 'Copiar exitosamente',
      })
    } catch (err) {
      console.log(err)
    }
  }

  const onSubmit = useCallback(
    async ({ email }: z.infer<typeof emailSchema>) => {
      try {
        loading.show()
        const resp = await subscribeEmail({
          email,
        }).unwrap()

        if (resp && resp.subscribeEmailToNewsletter?.status) {
          toast.show({
            icon: 'success',
            content: 'Suscripción exitosa',
          })
        }
      } catch (error) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(error as TCatchMessage),
        })
      } finally {
        loading.hide()
      }
    },
    [subscribeEmail, loading, toast],
  )

  /**
   * 格式化产品数据
   */
  const getBundleProduct = useCallback((options: unknown, id: unknown) => {
    if (!options || !Array.isArray(options)) return []
    return options.reduce((pre, item) => {
      if (item.product && item.product?.saleable_stock > 0) {
        pre.push({
          id,
          value: item.id,
          sku: item.product?.sku || '',
          qty: item.quantity || 1,
          name: item.product?.name || '',
          small_image: item.product?.small_image.url || '',
          images: item.product?.media_gallery.map((i: { url: string }) => i.url),
          price: item.price || 0,
          sub_name: item.product?.sw_simple_description || '',
          isSaleable: item.product?.saleable_message?.is_saleable || false,
          description: item.product?.short_description?.html || '',
          is_default: item.is_default || false,
          url_key: item.product?.url_key || '',
          categories: item.product?.categories || [],
          gtm_item_categories: item.product?.gtm_item_categories || null,
        })
      }
      return pre
    }, []) as BundleProducts[]
  }, [])

  /**
   * bundle 产品列表
   */
  const bundleProducts = useMemo(() => {
    if (!isBundle || !product) return []

    const result = (product as BundleProduct).items?.reduce<BundleProducts[]>((pre, item) => {
      if (item?.options && !item.required) {
        pre.push(...getBundleProduct(item.options, item.option_id))
      }
      return pre
    }, [])

    return result || []
  }, [isBundle, product, getBundleProduct])

  /**
   * 选择的bundle sku
   */
  const selectSku = useMemo(() => {
    return selectedBundle.map((i) => i.sku)
  }, [selectedBundle])

  /**
   * 选中/取消 bundle产品
   */
  const toggleSelectedBundle = (item: BundleProducts) => {
    if (selectSku.includes(item.sku)) {
      setSelectedBundle(selectedBundle.filter((i) => i.sku !== item.sku))
    } else {
      setSelectedBundle((pre) => {
        return [...pre, item]
      })
    }
  }

  /**
   * 清除选择产品
   */
  // const clearBundleProduct = () => {
  //   setSelectedBundle([])
  //   document.getElementById('pdp-checkout')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  // }

  useEffect(() => {
    if (!!Object.keys(errors)?.length) {
      toast.show({
        icon: 'fail',
        content: 'Invalid email address',
      })
    }
  }, [errors, toast])

  useEffect(() => {
    if (bundleProducts.length > 0) {
      setSelectedBundle(bundleProducts.filter((i) => i.is_default))
    }
  }, [bundleProducts, setSelectedBundle])

  return (
    <div>
      {/* Title */}
      <h3 className="font-miSansSemiBold520 text-2xl xl:text-[30px] xl:leading-[48px]">
        {product?.name}
      </h3>

      {/*  Price */}
      <div className="mt-[16px] flex items-center justify-between xl:mt-9">
        <div className="flex items-center gap-2 xl:gap-4">
          {product?.price_range && (
            <PriceRange
              priceRange={product.price_range}
              priceProps={{
                textStyle: `text-[16px] xs:text-[20px] xl:text-4xl`,
                currencyStyle: 'xl:text-lg text-base mr-[2px]',
                fractionStyle: `text-[16px] xs:text-[20px] xl:text-4xl`,
              }}
              originalPriceProps={{
                textStyle: `text-[16px] xs:text-[20px] xl:text-4xl`,
                currencyStyle: 'xl:text-lg text-base mr-[2px]',
                fractionStyle: `text-[16px] xs:text-[20px] xl:text-4xl`,
              }}
            />
          )}

          {storeConfig?.is_price_include_tax && (
            <span className="text-[12px] text-base text-[#86868B]">({t('vat_included')})</span>
          )}
        </div>
        <p
          className={cn(
            'flex items-center gap-2 text-sm text-gray-6 xl:text-lg xl:text-gray-6',
            responsive && responsive.xl && (isInStock ? '!text-gray-8' : '!text-gray-4'),
            // isInStock ? '' : 'opacity-40',
          )}>
          <div className="hidden xl:block">
            {responsive && responsive.xl && isInStock ? <InStock /> : <OutOfStock />}
          </div>
          <div>
            {isInStock ? (
              <span className="xl:text-[#0F0F0F]">{tCommon('in_stock')}</span>
            ) : (
              <span>{tCommon('out_of_stock')}</span>
            )}
          </div>
        </p>
      </div>

      {/* affirm */}
      {isDefined(productAffirmData) &&
        isDefined(productAffirmData?.product_image) &&
        productAffirmData?.product_image !== '' && (
          <div className="mt-[14px] hidden min-h-[52px] rounded-[8px] bg-gray-0 p-2 xl:mt-9 xl:p-[16px]">
            <AffirmWidget totalPrice={totalPrice} affirmData={productAffirmData} />
          </div>
        )}

      {product?.additional?.html && (
        <div className="mt-[14px] min-h-[52px] rounded-[8px] bg-gray-0 p-2 xl:mt-9 xl:p-[16px]">
          <div dangerouslySetInnerHTML={{ __html: product?.additional?.html || '' }} />
        </div>
      )}

      {/* reviews */}
      {isShowReviewWidget && product?.id && <ReviewTotalWidget productId={product?.id} />}

      <div className="mb-[18px] mt-[20px] h-[1px] bg-gray-1"></div>

      {/* Description */}
      {product?.short_description?.html && (
        <div>
          <h4 className="font-miSansSemiBold520 text-xl leading-[24px] text-gray-8 xl:font-miSansBold630 xl:text-2xl xl:leading-[27px]">
            {tCommon('description')}
          </h4>
          <ExpandableHTML htmlContent={product?.short_description?.html} />
        </div>
      )}

      {/* Product Parameter*/}
      <div id="product-parameter"></div>

      {/* Select Option */}
      {!!product?.select_option_products?.length && (
        <div>
          <h4 className="mb-4 mt-[14px] font-miSansSemiBold520 text-xl leading-[24px] text-gray-8 xl:mb-7 xl:mt-[52px] xl:font-miSansBold630 xl:text-2xl xl:leading-[27px]">
            {tCommon('select_option')}
          </h4>
          {[baseProductSelectOption, ...product?.select_option_products]?.map((option) => (
            <div
              key={option?.sku}
              onClick={() => {
                if (product?.sku !== option?.sku) {
                  router.push(`/${option?.url_key || ''}`)
                }
              }}
              className={cn(
                'my-3 rounded-md border p-[11px] hover:bg-gray-0 focus:bg-gray-1 xl:my-4 xl:p-[15px]',
                product?.sku === option?.sku
                  ? 'border-primary bg-white p-[10px] xl:border-[2px] xl:p-[13px]'
                  : 'cursor-pointer border-gray-2',
              )}>
              <div className="flex flex-1 gap-3">
                {responsive && responsive.xl && (
                  <Image
                    src={option?.small_image?.url || ''}
                    alt="product"
                    width={120}
                    height={120}
                    style={{ objectFit: 'contain' }}
                  />
                )}

                {responsive && !responsive.xl && (
                  <Image
                    src={option?.small_image?.url || ''}
                    alt="product"
                    width={75}
                    height={75}
                    style={{ objectFit: 'contain' }}
                  />
                )}

                <div>
                  <p className="font-miSansDemiBold450 text-[15px] leading-8 text-gray-8 xl:text-xl xl:leading-[24px]">
                    {option?.name}
                  </p>
                  <p className="mt-2 font-miSansMedium380 text-base leading-[19px] text-gray-5 xl:mt-3">
                    {option?.sw_simple_description}
                  </p>
                  <div className="mt-2 xl:mt-3">
                    {option?.price_range && (
                      <PriceRange
                        priceRange={option.price_range}
                        priceProps={{
                          textStyle: 'xl:font-miSansMedium380',
                        }}
                        originalPriceProps={{
                          textStyle: 'xl:font-miSansMedium380',
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bundle Products */}
      {!!bundleProducts?.length && isInStock && (
        <div>
          <h4 className="mb-4 mt-9 font-miSansSemiBold520 text-xl leading-[24px] text-gray-8 xl:mb-[24px] xl:font-miSansBold630 xl:text-2xl xl:leading-[27px]">
            {tCommon('bundle_products')}
          </h4>
          {bundleProducts.map((item) => {
            return (
              <div
                key={item.sku}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md border p-[11px] xl:my-4 xl:gap-3 xl:p-[15px]',
                  selectSku.includes(item.sku)
                    ? 'border-primary p-[10px] xl:border-[2px] xl:p-[14px]'
                    : 'border-gray-2',
                  responsive && responsive.xl ? 'my-3' : 'my-[10px]',
                )}
                onClick={() => toggleSelectedBundle(item)}>
                <Image
                  src={item?.small_image}
                  alt="product"
                  width={100}
                  height={100}
                  style={{ objectFit: 'contain' }}
                />
                <div className="flex-1 text-base">
                  <div className="flex flex-col justify-between border-b border-gray-1 pb-2 xl:flex-row xl:pb-[10px]">
                    <p className="font-miSansDemiBold450 text-base leading-[19px] xl:text-pc-lg xl:leading-[21px]">
                      {item?.name}
                    </p>
                    <div className="mt-2 xl:ml-1 xl:mt-0">
                      <Price
                        price={{ value: item?.price || 0 }}
                        currencyStyle="xl:text-lg text-base"
                        textStyle="xl:text-lg text-base"
                        fractionStyle="xl:text-lg text-base"
                      />
                    </div>
                  </div>
                  {item.sub_name && (
                    <p className="mt-2 font-miSansMedium380 text-base leading-[16px] text-gray-5 xl:mt-[10px] xl:leading-[19px]">
                      {item.sub_name}
                    </p>
                  )}
                  <div
                    className={cn(
                      'mt-2 inline-flex items-center gap-1 xl:mt-[5px]',
                      !item.sub_name ? 'mt-9 xl:mt-[47px]' : '',
                    )}
                    onClick={(e) => {
                      e.stopPropagation()
                      setTempProduct(item)
                      toggleDetailPopup()
                    }}>
                    <span className="font-miSansRegular330 text-sm leading-[16px] text-gray-8 xl:text-base xl:leading-[19px]">
                      Detail
                    </span>
                    <Arrow width={16} height={16} color="#6E6E73" />
                  </div>
                </div>
              </div>
            )
          })}

          {/* hide */}
          {/* <div
            onClick={clearBundleProduct}
            className={cn(
              'flex h-[60px] cursor-pointer items-center justify-center rounded-md border xl:h-[88px]',
              !selectSku.length ? 'border-primary xl:border-[2px]' : 'border-gray-2',
            )}>
            <span className="font-miSansSemiBold520 text-lg">No, thanks</span>
          </div> */}
        </div>
      )}

      {/* Coupon Code & Subscribe */}
      {couponData?.is_enable === '1' || pdpSubscriptionEnableCode === '1' ? (
        <div className="relative mt-[20px] overflow-hidden rounded-lg xl:mt-9">
          {couponData?.is_enable === '1' && couponData?.code && (
            <div className="flex items-center justify-center gap-2 rounded-t-lg border-b border-b-white bg-gray-1 px-3 py-4 xl:gap-8 xl:px-6">
              <div className="flex items-center gap-2 xl:gap-3">
                <CouponIcon />
                <div
                  className="font-miSansDemiBold450 text-base xl:text-lg xl:leading-[28px]"
                  dangerouslySetInnerHTML={{ __html: couponData?.title || '' }}></div>
              </div>
              <div className="cursor-pointer" onClick={handleCopy}>
                <CopyIcon
                  width={responsive && responsive.xl ? 28 : 24}
                  height={responsive && responsive.xl ? 28 : 24}
                />
              </div>
            </div>
          )}

          {pdpSubscriptionEnableCode === '1' && (
            <div
              className={cn(
                'relative overflow-hidden rounded-b-lg bg-gray-1',
                couponData?.code ? '' : 'rounded-t-lg',
              )}>
              {subscriptionBlockData?.background_pc && responsive && responsive.xl && (
                <Image
                  src={subscriptionBlockData?.background_pc || ''}
                  alt="subscribe"
                  fill
                  style={{ objectFit: 'contain' }}
                />
              )}
              {subscriptionBlockData?.background_mob && responsive && !responsive.xl && (
                <Image
                  src={subscriptionBlockData?.background_mob || ''}
                  alt="subscribe"
                  fill
                  style={{ objectFit: 'contain', objectPosition: 'right' }}
                />
              )}

              <div className="relative z-0 px-4 pt-4 text-center font-miSansDemiBold450 text-base xl:px-6 xl:pt-6 xl:text-lg">
                {subscriptionBlockData?.title}
              </div>
              <form
                className="relative z-0 flex justify-center py-4 xl:pb-6"
                onSubmit={handleSubmit((data) => {
                  onSubmit(data)
                })}>
                <Input
                  {...register('email')}
                  className="h-[36px] w-[212px] rounded-bl-[8px] rounded-br-[0px] rounded-tl-[8px] rounded-tr-[0px] border-white bg-white placeholder:text-gray-4 xl:h-[40px] xl:w-[278px]"
                  placeholder={tCommon('your_email')}
                />
                <Button
                  variant="default"
                  type="submit"
                  disabled={isLoading}
                  className="h-[36px] w-[36px] rounded-l-[0px] rounded-r-[8px] bg-[#000000] hover:bg-[#0F0F0F] focus:bg-[#000000] active:bg-[#000000] xl:h-[40px] xl:w-[40px]">
                  <div className="flex-none">
                    <Arrow
                      width={responsive && responsive.xl ? 24 : 20}
                      height={responsive && responsive.xl ? 24 : 20}
                      color="#ffffff"
                    />
                  </div>
                </Button>
              </form>
            </div>
          )}
        </div>
      ) : null}

      {!isInStock && (
        <div className="xl:mt-9 xl:rounded-lg xl:bg-gray-0 xl:p-6">
          {responsive && responsive.xl && (
            <p className="font-miSansMedium380 text-base text-black">
              {tCommon('please_other_size_color')}
            </p>
          )}
          <div
            onClick={() => {
              if (isSubscribe) {
                return
              }
              addProductAlert(Number(decodeBase64(product?.uid || '')))
            }}
            className={cn(
              'm-auto mt-6 flex w-full items-center gap-2 rounded-md border border-gray-2 px-7 py-2 xl:w-max xl:px-9 xl:py-3',
              isSubscribe ? 'cursor-default' : 'cursor-pointer',
            )}>
            <EmailIcon width={20} height={20} color={isSubscribe ? '#86868B ' : '#000000'} />
            <p
              className={cn(
                'whitespace-normal break-words font-miSansMedium380 text-base text-black',
                isSubscribe ? 'text-gray-4' : 'text-black',
              )}>
              {isSubscribe
                ? '¡Gracias! Te avisaremos cuando esté disponible.'
                : 'Avísame cuando este producto esté disponible'}
            </p>
          </div>
        </div>
      )}

      <DetailPopup open={showDetailPopup} onOpenChange={toggleDetailPopup} product={tempProduct} />
    </div>
  )
}

export default ProductInfo
